import { generatePreSignedUrl } from '../services/upload/generatePreSignedUrl';
import { getUploadId } from '../services/upload/getUploadId';
import { uploadToPresignedUrl } from '../services/upload/uploadFileToPresignedUrl';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUuid = (value?: string) => !!value && UUID_REGEX.test(value);

const getCandidateIdFromUrl = (sourceUrl: string) => {
  try {
    const pathname = new URL(sourceUrl).pathname;
    const parts = pathname.split('/');
    return parts[parts.length - 1] || '';
  } catch {
    return '';
  }
};

const getProtocol = (sourceUrl: string) => {
  try {
    return new URL(sourceUrl).protocol;
  } catch {
    return '';
  }
};

interface ResolveMediaUploadIdParams {
  sourceUrl: string;
  blob: Blob;
  format: 'image' | 'video';
  fileName?: string;
  candidateId?: string;
  signal?: AbortSignal;
}

export const resolveMediaUploadId = async ({
  sourceUrl,
  blob,
  format,
  fileName,
  candidateId,
  signal,
}: ResolveMediaUploadIdParams): Promise<string> => {
  const inferredId = candidateId ?? getCandidateIdFromUrl(sourceUrl);
  const sourceProtocol = getProtocol(sourceUrl);

  const shouldCreateUpload =
    sourceProtocol === 'blob:' || sourceProtocol === 'data:' || !isUuid(inferredId);

  if (!shouldCreateUpload) {
    return inferredId;
  }

  const mimeType = blob.type || (format === 'image' ? 'image/jpeg' : 'video/mp4');
  const uploadFileName =
    fileName || (format === 'image' ? 'selected-image.jpg' : 'selected-video.mp4');
  const file = new File([blob], uploadFileName, { type: mimeType });

  const {
    uploadId: multiPartUploadId,
    preSignedUrls,
    format: uploadFormat,
  } = await generatePreSignedUrl(file);

  await uploadToPresignedUrl({
    uploadId: multiPartUploadId,
    presignedUrls: preSignedUrls,
    file,
    signal,
  });

  const uploadRes = await getUploadId({
    url: preSignedUrls[0].url,
    format: uploadFormat,
    mimeType,
    signal,
  });

  return uploadRes?.uploadId || '';
};
